import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';

type NotificationStatus = 'all' | 'unread' | 'read';

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-screen pb-24 lg:pb-8" style="background:#F4F6F9;">
      <header class="sticky top-0 z-30 bg-[#102C57] text-white border-b border-white/10">
        <div class="h-16 px-4 lg:px-7 flex items-center justify-between">
          <div class="flex items-center gap-3 min-w-0">
            <a routerLink="/" class="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center active:scale-95">
              <i class="ti ti-chevron-left text-xl"></i>
            </a>
            <div class="min-w-0">
              <p class="text-[10px] font-bold uppercase tracking-widest text-white/45">Mobile Inbox</p>
              <h1 class="text-lg font-bold leading-tight">Notification Center</h1>
            </div>
          </div>

          <button
            type="button"
            (click)="markAllRead()"
            [disabled]="isSaving() || unreadCount() === 0"
            class="h-9 px-3 rounded-full bg-white text-[#102C57] text-xs font-bold disabled:opacity-45">
            Mark all read
          </button>
        </div>
      </header>

      <main class="px-4 lg:px-7 pt-4 max-w-5xl mx-auto space-y-4">
        <section class="grid grid-cols-3 gap-2">
          <div class="metric">
            <span>Unread</span>
            <strong>{{ unreadCount() }}</strong>
          </div>
          <div class="metric">
            <span>Priority</span>
            <strong class="text-rose-600">{{ highPriorityCount() }}</strong>
          </div>
          <div class="metric">
            <span>Showing</span>
            <strong>{{ filteredNotifications().length }}</strong>
          </div>
        </section>

        <section class="bg-white rounded-2xl border border-slate-100 p-3 space-y-3">
          <div class="relative">
            <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]"></i>
            <input type="text" [ngModel]="search()" (ngModelChange)="search.set($event)" placeholder="Search alerts..."
                   class="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 outline-none focus:border-[#1A6FA6] focus:bg-white">
          </div>
          <div class="grid grid-cols-2 gap-2">
            <select [ngModel]="status()" (ngModelChange)="status.set($event)" class="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-600 outline-none">
              <option value="all">All statuses</option>
              <option value="unread">Unread only</option>
              <option value="read">Read only</option>
            </select>
            <select [ngModel]="priority()" (ngModelChange)="priority.set($event)" class="h-10 rounded-xl border border-slate-200 bg-slate-50 px-3 text-xs font-semibold text-slate-600 outline-none">
              <option value="">All priorities</option>
              <option value="high">High priority</option>
              <option value="normal">Normal</option>
              <option value="low">Low</option>
            </select>
          </div>
        </section>

        <section class="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 lg:mx-0 lg:px-0">
          <button type="button" (click)="selectedCategory.set('all')" class="category-pill" [ngClass]="selectedCategory() === 'all' ? 'active' : ''">
            <i class="ti ti-inbox"></i>
            All
            <b>{{ notifications().length }}</b>
          </button>
          @for (category of categories; track category.key) {
            <button type="button" (click)="selectedCategory.set(category.key)" class="category-pill" [ngClass]="selectedCategory() === category.key ? 'active' : ''">
              <i class="ti" [class]="'ti-' + category.icon"></i>
              {{ category.short }}
              <b>{{ categoryCount(category.key) }}</b>
            </button>
          }
        </section>

        @if (isLoading()) {
          <div class="bg-white rounded-2xl border border-slate-100 p-6 text-center text-slate-500">
            <i class="ti ti-loader-2 animate-spin text-2xl"></i>
            <p class="text-sm font-semibold mt-2">Loading notifications</p>
          </div>
        } @else if (filteredNotifications().length === 0) {
          <div class="bg-white rounded-2xl border border-slate-100 p-8 text-center">
            <div class="w-14 h-14 rounded-2xl bg-slate-50 text-slate-400 mx-auto flex items-center justify-center">
              <i class="ti ti-bell-off text-2xl"></i>
            </div>
            <h2 class="text-base font-bold text-slate-900 mt-4">No notifications found</h2>
            <p class="text-sm text-slate-500 mt-1">Try another category, priority, status, or search.</p>
          </div>
        } @else {
          <div class="space-y-3">
            @for (item of filteredNotifications(); track item.id) {
              <article
                class="bg-white rounded-2xl border p-4 transition-all"
                [class.border-[#1A6FA6]]="!item.read_at"
                [class.shadow-sm]="!item.read_at"
                [class.border-slate-100]="item.read_at">
                <div class="flex gap-3">
                  <div class="w-11 h-11 rounded-2xl flex items-center justify-center shrink-0" [ngClass]="iconClass(item)">
                    <i class="ti text-xl" [class]="'ti-' + icon(item)"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-start justify-between gap-3">
                      <div class="min-w-0">
                        <div class="flex flex-wrap gap-1.5 mb-1">
                          <span class="tag" [ngClass]="categoryTagClass(item.category)">{{ categoryLabel(item.category) }}</span>
                          <span class="tag" [ngClass]="priorityTagClass(item.priority)">{{ item.priority || 'normal' }}</span>
                          @if (!item.read_at) {
                            <span class="tag bg-[#E8F4FB] text-[#1A6FA6]">Unread</span>
                          }
                        </div>
                        <h2 class="text-sm font-bold text-slate-900 leading-snug">{{ item.title || 'Notification' }}</h2>
                      </div>
                      @if (!item.read_at) {
                        <span class="w-2 h-2 mt-1.5 rounded-full bg-[#1A6FA6] shrink-0"></span>
                      }
                    </div>
                    <p class="text-sm text-slate-600 mt-1 leading-snug">{{ item.body || 'Open for details.' }}</p>
                    <div class="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[11px] font-semibold text-slate-400">
                      <span><i class="ti ti-clock"></i> {{ timeLabel(item.created_at) }}</span>
                      @if (item.reference_type || item.reference_id) {
                        <span><i class="ti ti-tag"></i> {{ item.reference_type || 'Ref' }} {{ item.reference_id || '' }}</span>
                      }
                    </div>
                  </div>
                </div>
                <div class="mt-3 grid grid-cols-2 gap-2">
                  <button type="button" (click)="openNotification(item)" class="h-10 rounded-xl bg-[#102C57] text-white text-xs font-bold flex items-center justify-center gap-2 active:scale-[0.99]">
                    <i class="ti ti-external-link"></i>
                    Open
                  </button>
                  <button type="button" (click)="markRead(item)" [disabled]="item.read_at" class="h-10 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 text-xs font-bold flex items-center justify-center gap-2 disabled:opacity-45 active:scale-[0.99]">
                    <i class="ti ti-check"></i>
                    Mark read
                  </button>
                </div>
              </article>
            }
          </div>
        }
      </main>
    </div>
  `,
  styles: [`
    .metric { background:#fff; border:1px solid #e2e8f0; border-radius:16px; padding:12px; min-width:0; }
    .metric span { display:block; font-size:10px; font-weight:800; text-transform:uppercase; letter-spacing:.1em; color:#94a3b8; }
    .metric strong { display:block; margin-top:4px; font-size:22px; line-height:1; color:#0f172a; }
    .category-pill { height:38px; display:inline-flex; align-items:center; gap:7px; padding:0 12px; border-radius:999px; background:#fff; border:1px solid #e2e8f0; color:#475569; font-size:12px; font-weight:800; white-space:nowrap; }
    .category-pill.active { background:#102C57; color:#fff; border-color:#102C57; }
    .category-pill b { min-width:20px; height:20px; padding:0 6px; border-radius:999px; display:inline-flex; align-items:center; justify-content:center; background:#f1f5f9; color:#475569; font-size:10px; }
    .category-pill.active b { background:rgba(255,255,255,.16); color:#fff; }
    .tag { display:inline-flex; align-items:center; height:20px; border-radius:999px; padding:0 8px; font-size:10px; font-weight:800; text-transform:capitalize; }
  `]
})
export class NotificationsComponent implements OnInit {
  private api = inject(ApiService);
  private router = inject(Router);

  notifications = signal<any[]>([]);
  unreadCount = signal(0);
  isLoading = signal(true);
  isSaving = signal(false);
  selectedCategory = signal('all');
  status = signal<NotificationStatus>('all');
  priority = signal('');
  search = signal('');

  categories = [
    { key: 'assignments', short: 'Assignments', label: 'Assignments', icon: 'briefcase' },
    { key: 'schedule', short: 'Schedule', label: 'Schedule', icon: 'calendar-time' },
    { key: 'leave', short: 'Leave', label: 'Leave', icon: 'calendar-plus' },
    { key: 'fleet', short: 'Fleet', label: 'Fleet', icon: 'truck' },
    { key: 'safety', short: 'Safety', label: 'Safety', icon: 'shield-check' },
    { key: 'quotes_feedback', short: 'Quotes', label: 'Quotes & Feedback', icon: 'message-star' },
    { key: 'documents', short: 'Docs', label: 'Documents', icon: 'folders' },
    { key: 'system', short: 'System', label: 'System', icon: 'bell' }
  ];

  highPriorityCount = computed(() => this.notifications().filter(item => item.priority === 'high').length);
  filteredNotifications = computed(() => {
    const selected = this.selectedCategory();
    const searchTerm = this.search().trim().toLowerCase();
    return this.notifications().filter(item => {
      if (selected !== 'all' && item.category !== selected) return false;
      if (this.status() === 'unread' && item.read_at) return false;
      if (this.status() === 'read' && !item.read_at) return false;
      if (this.priority() && item.priority !== this.priority()) return false;
      if (!searchTerm) return true;
      const haystack = [item.title, item.body, item.type, item.reference_type, item.reference_id, item.route, item.category].join(' ').toLowerCase();
      return haystack.includes(searchTerm);
    });
  });

  ngOnInit() {
    this.loadNotifications();
  }

  loadNotifications() {
    this.isLoading.set(true);
    this.api.getNotifications({ limit: 300 }).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
          const items = (res.data?.items || []).map((item: any) => ({ ...item, category: this.mobileCategory(item) }));
          this.notifications.set(items);
          this.unreadCount.set(Number(res.data?.unread_count || items.filter((item: any) => !item.read_at).length));
        } else {
          showToast('error', res.message || 'Unable to load notifications.');
        }
      },
      error: (err: any) => {
        this.isLoading.set(false);
        showToast('error', err?.error?.message || 'Unable to load notifications.');
      }
    });
  }

  openNotification(item: any) {
    const route = this.routeFor(item);
    const go = () => this.router.navigateByUrl(route);
    if (item?.id && !item.read_at) {
      this.api.markNotificationRead(item.id).subscribe({
        next: () => {
          this.applyRead(item.id);
          go();
        },
        error: () => go()
      });
      return;
    }
    go();
  }

  markRead(item: any) {
    if (!item?.id || item.read_at) return;
    this.api.markNotificationRead(item.id).subscribe({
      next: () => this.applyRead(item.id),
      error: (err: any) => showToast('error', err?.error?.message || 'Unable to update notification.')
    });
  }

  markAllRead() {
    if (this.isSaving() || this.unreadCount() === 0) return;
    this.isSaving.set(true);
    this.api.markAllNotificationsRead().subscribe({
      next: () => {
        this.isSaving.set(false);
        const now = new Date().toISOString();
        this.notifications.update(items => items.map(item => ({ ...item, read_at: item.read_at || now })));
        this.unreadCount.set(0);
      },
      error: (err: any) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Unable to update notifications.');
      }
    });
  }

  routeFor(item: any): string {
    const type = String(item?.reference_type || '').toLowerCase();
    const ref = String(item?.reference_id || '').trim();
    if (type === 'job' && ref) return `/job/${encodeURIComponent(ref)}`;
    if (type === 'project' && ref) return `/project/${encodeURIComponent(ref)}`;
    if (type === 'leaverequest' && ref) return `/leave-requests/${encodeURIComponent(ref)}`;

    const route = String(item?.route || '').trim();
    if (route.startsWith('/job/') || route.startsWith('/project/') || route.startsWith('/leave-requests') || route.startsWith('/as-built/')) {
      return route;
    }
    return '/';
  }

  categoryCount(category: string): number {
    return this.notifications().filter(item => item.category === category).length;
  }

  categoryLabel(category: string): string {
    return this.categories.find(item => item.key === category)?.label || 'System';
  }

  categoryTagClass(category: string): string {
    const classes: Record<string, string> = {
      assignments: 'bg-sky-50 text-[#1A6FA6]',
      schedule: 'bg-indigo-50 text-indigo-600',
      leave: 'bg-amber-50 text-amber-600',
      fleet: 'bg-teal-50 text-teal-600',
      safety: 'bg-rose-50 text-rose-600',
      quotes_feedback: 'bg-emerald-50 text-emerald-600',
      documents: 'bg-violet-50 text-violet-600',
      system: 'bg-slate-100 text-slate-600'
    };
    return classes[category] || classes['system'];
  }

  priorityTagClass(priority: string): string {
    if (priority === 'high') return 'bg-rose-50 text-rose-600';
    if (priority === 'low') return 'bg-slate-100 text-slate-500';
    return 'bg-emerald-50 text-emerald-600';
  }

  icon(item: any): string {
    const category = item.category || this.mobileCategory(item);
    if (category === 'assignments') return 'briefcase';
    if (category === 'schedule') return 'calendar-time';
    if (category === 'leave') return 'calendar-plus';
    if (category === 'fleet') return 'truck';
    if (category === 'safety') return 'shield-check';
    if (category === 'quotes_feedback') return 'message-star';
    if (category === 'documents') return 'folders';
    return 'bell';
  }

  iconClass(item: any): string {
    if (String(item?.priority || '').toLowerCase() === 'high') return 'bg-rose-50 text-rose-600';
    return this.categoryTagClass(item.category || this.mobileCategory(item)).replace(' text-', ' text-');
  }

  timeLabel(value: string): string {
    const date = value ? new Date(String(value).replace(' ', 'T')) : null;
    if (!date || Number.isNaN(date.getTime())) return '';
    const seconds = Math.max(0, Math.floor((Date.now() - date.getTime()) / 1000));
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
    return date.toLocaleDateString('en-NZ', { timeZone: 'Pacific/Auckland' });
  }

  private applyRead(id: number | string) {
    const now = new Date().toISOString();
    this.notifications.update(items => items.map(item => item.id === id ? { ...item, read_at: item.read_at || now } : item));
    this.unreadCount.update(count => Math.max(0, count - 1));
  }

  private mobileCategory(item: any): string {
    const backendCategory = String(item?.category || '').toLowerCase();
    if (backendCategory === 'fleet') return 'fleet';
    if (backendCategory === 'compliance') return 'safety';
    if (backendCategory === 'financials' || backendCategory === 'customer_experience') return 'quotes_feedback';
    if (backendCategory === 'projects_documents') return 'documents';

    const text = [item?.type, item?.reference_type, item?.route, item?.title].join(' ').toLowerCase();
    if (/fleet|vehicle|truck|van/.test(text)) return 'fleet';
    if (/leave/.test(text)) return 'leave';
    if (/safety|audit|visa|compliance/.test(text)) return 'safety';
    if (/quote|feedback|customer/.test(text)) return 'quotes_feedback';
    if (/document|phase|declaration|as.built|as-built|insurance/.test(text)) return 'documents';
    if (/shift|schedule|calendar|timesheet/.test(text)) return 'schedule';
    if (/assignment|job|project|work/.test(text)) return 'assignments';
    return 'system';
  }
}
